// @ts-strict-ignore
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';

import {ReportName} from 'common/report/constants';
import {Persistence} from 'common/report/persistence';
import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {objToQueryString} from 'common/util';
import {apiClient} from 'common/util/api-client';
import {findKeyByValue} from 'common/util/typescript';
import {getDashboardAllowedReports} from 'common/report/util/boards';
import {Bookmark, BookmarkParamsUnionType, TemplateSpecs} from 'common/report/dashboards/types';
import {TopCreator} from 'common/report/queries/app-api/project-top-creators';
import {getEntityCreatorDisplayName, isSystemUser} from 'common/report/stores/user-details';
import {CurrentProject} from 'common/report/model-resources/projects';
import {getLocation} from 'common/util/url';
import {hasAccessToFeatureFlagsApp} from 'common/util/feature-flags-feature-gate';
import {hasAccessToExperimentsApp} from 'common/util/experiments-feature-gate';

import {FeatureRollouts, isCanvasEnabled} from '../feature-sampling';
import {
  RecentSearchItem,
  RecentSearchItemType,
  SearchDisplayContext,
  SearchDisplayContextIcons,
  SearchQueryFilters,
  SearchQuerySort,
  SearchResult,
  SearchResultType,
  SearchSortOption,
  TableSort,
} from './types';

export const searchUtils = {
  getRecentItems,
  addRecentItem,
  removeRecentItem,
  executeSearch,
  getTemplateItems,
};

export function getDisplayContext(item: SearchResult): SearchDisplayContext {
  if (item.type === SearchResultType.Dashboard) {
    return SearchDisplayContext.Dashboard;
  }
  return item.report as SearchDisplayContext;
}

export function getSearchIconFromDisplayContext(displayContext): SearchDisplayContextIcons {
  return SearchDisplayContextIcons[findKeyByValue(SearchDisplayContext, displayContext)];
}

export function getSearchContexts() {
  return Object.values(SearchDisplayContext);
}

// consolidate report name and search contexts ideally
export function getReportNameFromSearchContext(displayContext: SearchDisplayContext): ReportName {
  const contextsToReports = {
    [SearchDisplayContext.Dashboard]: ReportName.Dashboards,
    [SearchDisplayContext.Flows]: ReportName.Flows,
    [SearchDisplayContext.Funnels]: ReportName.Funnels,
    [SearchDisplayContext.Impact]: ReportName.Impact,
    [SearchDisplayContext.Insights]: ReportName.Insights,
    [SearchDisplayContext.Retention]: ReportName.Retention,
    [SearchDisplayContext.HeatMap]: ReportName.HeatMaps,
    [SearchDisplayContext.HeatMapCollection]: ReportName.HeatMapCollections,
    [SearchDisplayContext.MetricTree]: ReportName.Canvas,
    [SearchDisplayContext.Experiment]: ReportName.Experiments,
    [SearchDisplayContext.FeatureFlag]: ReportName.FeatureFlags,
    [SearchDisplayContext.Playlist]: ReportName.Playlists,
  };
  return contextsToReports[displayContext];
}

export function getSearchContextFromReportName(reportName: ReportName): SearchDisplayContext {
  const reportsToContexts = {
    [ReportName.Dashboards]: SearchDisplayContext.Dashboard,
    [ReportName.Flows]: SearchDisplayContext.Flows,
    [ReportName.Funnels]: SearchDisplayContext.Funnels,
    [ReportName.Impact]: SearchDisplayContext.Impact,
    [ReportName.Insights]: SearchDisplayContext.Insights,
    [ReportName.Retention]: SearchDisplayContext.Retention,
    [ReportName.HeatMaps]: SearchDisplayContext.HeatMap,
    [ReportName.HeatMapCollections]: SearchDisplayContext.HeatMapCollection,
    [ReportName.Canvas]: SearchDisplayContext.MetricTree,
    [ReportName.Experiments]: SearchDisplayContext.Experiment,
    [ReportName.FeatureFlags]: SearchDisplayContext.FeatureFlag,
    [ReportName.Playlists]: SearchDisplayContext.Playlist,
  };
  return reportsToContexts[reportName];
}

export function getOrderedSearchContextDisplayOptions(
  currentProject: CurrentProject | null = null,
): SearchDisplayContext[] {
  return [
    ReportName.Dashboards,
    ...getDashboardAllowedReports(currentProject),
    isCanvasEnabled(currentProject) ? [ReportName.Canvas] : [],
    hasAccessToFeatureFlagsApp(currentProject) ? [ReportName.FeatureFlags] : [],
    hasAccessToExperimentsApp(currentProject) ? [ReportName.Experiments] : [],
    ReportName.Impact,
  ]
    .flat()
    .map((report) => getSearchContextFromReportName(report));
}

export function transformBookmarkIntoRecentItem<BookmarkParamsType extends BookmarkParamsUnionType>(
  bookmark: Bookmark<BookmarkParamsType>,
  endpoint: string,
  {projectId, workspaceId, userId},
): RecentSearchItem {
  // TODO: implement safer cast from BookmarkType => SearchDisplayContext (string values are the same)
  const type = bookmark.type as unknown as SearchDisplayContext;
  const modifiedUtc = moment.utc(bookmark.modified).local();

  const item = {
    description: bookmark.description,
    icon: getSearchIconFromDisplayContext(type),
    id: bookmark.id,
    isOwner: bookmark.creator_id === userId,
    key: `entity-${bookmark.id}`,
    label: bookmark.name,
    link: `${getReportUrl(`${endpoint}/#report/${bookmark.id}`, {projectId, workspaceId})}`,
    report: type,
    subLabel: `${bookmark.creator_name} : ${modifiedUtc.format(`MMM Do, YYYY`)}`,
    type: RecentSearchItemType.Entity,
    user: bookmark.creator_name,
  };

  return item;
}

// SEARCH PERSISTENCE

const contentDiscoveryPersistenceKey = `search`;
let persistence;

function getRecentItems(): RecentSearchItem[] {
  const projectId = getProjectId();
  // projectId is present on url 99% of the time but for some url e.g settings, it's not.
  // we have to wait for DOMContentLoaded for those cases, so create persistence if it's not already created
  // this check is done here because all persistence related queries stems in this function
  if (projectId) {
    persistence = Persistence.createCachedNamespace(projectId, contentDiscoveryPersistenceKey);
  }
  const items = persistence?.getObject(`recent-items`) || [];
  return items;
}

function addRecentItem(item: RecentSearchItem) {
  const items = searchUtils.getRecentItems();
  const recentItems = uniqBy([item, ...items], (recentItem) => recentItem.key).slice(0, 50);
  persistence?.setObject(`recent-items`, recentItems);
}

function removeRecentItem(item: RecentSearchItem) {
  const storedItems = searchUtils.getRecentItems();
  const recentItems = storedItems.filter((storedItem) => storedItem.key !== item.key);
  persistence?.setObject(`recent-items`, recentItems);
}

function getTemplateItems(searchedText: string, currentProject: CurrentProject) {
  const formattedSearchedText = searchedText ? searchedText.trim().toLowerCase() : ``;

  if (!formattedSearchedText) {
    return [];
  }

  return Object.values(TemplateSpecs)
    .filter(
      ({title, keywords, featureRolloutName}) =>
        (!featureRolloutName || FeatureRollouts[featureRolloutName].isEnabled(currentProject)) &&
        (title.toLowerCase().includes(formattedSearchedText) ||
          (keywords && keywords.some((keyword) => keyword.toLowerCase().includes(formattedSearchedText)))),
    )
    .map((template) => {
      const {title, type} = template;
      return {
        icon: `brand-dashboard`,
        label: title,
        value: {
          user: `Mixpanel`,
          email: `eng-accounts@mixpanel.com`,
        },
        // type expects a very specific string, but it could have spaces in it. encode it
        href: `${getReportUrl(`boards`, {
          projectId: getProjectId(),
          workspaceId: getWorkspaceId(),
        })}?show-template-selector=true&selected-template=${encodeURIComponent(type)}`,
      };
    });
}

export function removeRecentItemById(id: number) {
  const itemToBeRemoved = searchUtils.getRecentItems().find((item) => item.id === id);
  // Update LocalStorage
  if (itemToBeRemoved) {
    searchUtils.removeRecentItem(itemToBeRemoved);
  }
}

// SEARCH API

export const DEFAULT_SEARCH_QUERY_STRING = ``;
export const PROMPT_ALLOW_STORAGE_STRING = `search.allow-storage-prompt`;

export function buildSearchQuery(
  queryFilters: SearchQueryFilters,
  querySort: SearchQuerySort | TableSort,
  searchedText: string,
  limit: number,
  sharedOnly: boolean,
  queryEntities?: Array<{id: number; type: SearchResultType}>,
) {
  const queryParams = {
    query_string: searchedText,
    limit,
    shared_only: sharedOnly,
  };

  if (queryEntities) {
    queryParams[`query_entities`] = JSON.stringify(queryEntities);
  }

  if (queryFilters) {
    queryParams[`query_filters`] = JSON.stringify({...queryFilters});
  }

  if (querySort) {
    queryParams[`query_sort`] = JSON.stringify({...querySort});
  }

  return objToQueryString(queryParams);
}

export function getUrl(): string | null {
  const workspaceId = parseInt(getWorkspaceId(), 10) || null; // null instead of NaN
  const projectId = getProjectId();

  if (workspaceId) {
    return `/api/app/workspaces/${workspaceId}/search`;
  }

  if (projectId) {
    return `/api/app/projects/${getProjectId()}/search`;
  }

  return null;
}

async function loadResults(promise: Promise<any>, {replaceWithEmpty = true} = {}) {
  let results = await promise;
  // Only return if no further requests have been triggered
  if (results || replaceWithEmpty) {
    results = results.filter(
      (item) => ![SearchResultType.Help, SearchResultType.Message, SearchResultType.Campaign].includes(item.type),
    );
  }
  return results;
}

// TODO(Home) Refactor executeSearch to use react-query (see useRecentlyViewedItemsQuery)
// When using this function, be sure to check for race conditions by accepting the results only if the searched text
// matches the requested search text, or if your request index is the same
async function executeSearch({
  queryFilters,
  queryEntities,
  querySort,
  searchedText = ``,
  limit = 100,
  sharedOnly = true,
  fromFetchCache = true,
}: {
  queryFilters?: SearchQueryFilters;
  queryEntities?: Array<{id: number; type: SearchResultType}>;
  querySort?: SearchQuerySort | TableSort;
  searchedText?: string;
  limit?: number;
  sharedOnly?: boolean;
  fromFetchCache?: boolean;
}): Promise<SearchResult[]> {
  const endpoint = getUrl();

  if (!endpoint) {
    return [];
  }

  const url = `${endpoint}?${buildSearchQuery(queryFilters, querySort, searchedText, limit, sharedOnly, queryEntities)}`;

  if (!fromFetchCache) {
    return loadResults(apiClient.get<SearchResult[]>(url, null, {updateFetchCache: true}), {});
  }

  // Boosts performance by using cached results when available while
  // always triggering a network call in parallel
  const {cache, network} = apiClient.hydratedGet<SearchResult[]>(url, null);
  const results = await loadResults(cache, {replaceWithEmpty: false});
  if (results?.length) {
    return results;
  }
  return loadResults(network, {});
}

const TOTAL_CREATORS = 5;
const MAX_RECENT_CREATORS = 2;

export function getSuggestedCreators(userId: number, topCreators: TopCreator[]): TopCreator[] {
  if (topCreators?.length === 0 || !userId) {
    return [];
  }

  // we don't add users that have 0 creations
  let suggestedCreators: TopCreator[] = [];

  const meAsCreator: TopCreator = topCreators.find((creator) => creator.id === userId);
  if (meAsCreator) {
    suggestedCreators.push(meAsCreator);
  }

  const recentItems = searchUtils.getRecentItems();
  const recentUserItems = recentItems.filter((item) => item.type === RecentSearchItemType.User);

  const recentCreators = recentUserItems
    .filter((recentUserItem) => meAsCreator?.id !== recentUserItem.id && !isSystemUser({userId: recentUserItem.id}))
    .map((recentUserItem) => topCreators.find((creator) => creator.id === recentUserItem.id))
    .slice(0, MAX_RECENT_CREATORS);

  suggestedCreators = [...suggestedCreators, ...recentCreators].filter(Boolean);
  const suggestedCreatorIds = suggestedCreators.map((creator) => creator.id);

  // we fill with the top creators that are not me or already in the ranked creators
  const remainingCreators = topCreators
    .filter((creator) => !suggestedCreatorIds.includes(creator.id) && !isSystemUser({userId: creator.id}))
    .slice(0, TOTAL_CREATORS - suggestedCreators.length); // total - meAndSearchedCreators

  suggestedCreators = [...suggestedCreators, ...remainingCreators];
  if (suggestedCreators.length < TOTAL_CREATORS) {
    return [];
  }
  return suggestedCreators;
}

export const BROWSE_URL_PARAM = `discover`;

enum ExtraDefaultFilter {
  All = ``,
}

export type BrowseEntityTypeFilter = SearchDisplayContext | ExtraDefaultFilter;
export const BrowseEntityTypeFilter = {
  ...ExtraDefaultFilter,
  ...SearchDisplayContext,
};

type BrowseUrlParams = {
  q?: string;
  'entity-type-filter'?: BrowseEntityTypeFilter;
  'event-name-filter'?: string;
  email?: string;
  sort?: SearchSortOption;
};

export function isBrowseUrl() {
  const location = getLocation();
  return location.pathname.endsWith(`/app/boards`) && location.hash?.includes(`#${BROWSE_URL_PARAM}`);
}

export type GetBrowseUrlOptions = {
  queryParam?: BrowseUrlParams;
  resetFilters?: boolean;
  projectId?: number;
  workspaceId?: number;
};

export function getBrowseUrl(options: GetBrowseUrlOptions = {}) {
  let resetFilters;
  const currentLocation = getLocation();
  if (!(`resetFilters` in options) && !currentLocation.hash?.includes(BROWSE_URL_PARAM)) {
    resetFilters = true;
  } else {
    resetFilters = options.resetFilters;
  }

  const {queryParam} = options;
  const baseQueryParams = resetFilters
    ? {
        email: ``,
        q: ``,
        'entity-type-filter': BrowseEntityTypeFilter.All,
      }
    : {};

  const queryParams = {
    ...baseQueryParams,
    ...queryParam,
  };

  const url = getReportUrl(`boards#${BROWSE_URL_PARAM}`, {
    projectId: options.projectId ?? getProjectId(),
    workspaceId: options.workspaceId ?? getWorkspaceId(),
  });

  const params = new URLSearchParams(queryParams).toString();
  return url + (params ? `&${params}` : ``);
}

export function getCreatorNameLabel(creator: TopCreator, currentUserId: number) {
  const name = getEntityCreatorDisplayName(creator.email, creator.name);
  return name + (currentUserId === creator.id ? ` (you)` : ``);
}

export function appendBrowseSource(url: string): string {
  const urlObj = new URL(url, getLocation().href);
  urlObj.searchParams.set(BROWSE_URL_PARAM, `1`);
  return urlObj.toString();
}
